
import { defineComponent } from 'vue';
import FbxButton from '@/components/fbx-button.vue';
import FbxInput from '@/components/fbx-input.vue';
import VirtualRedTagHeader from '@/components/VirtualRedTag/VirtualRedTagHeader.vue';
import trailerByNumber from '@/shared/mixins/trailerByNumber';
import { mapGetters } from 'vuex';
import { ActionTypes } from '@/store/actionTypes';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import {
    isPlateMatch,
    isVinMatch,
    trailerHasLicensePlateNumber,
    trailerHasVin,
    vinOrPlateVerificationText,
} from '@/shared/trailerIdentityVerification';

export default defineComponent({
    name: 'VirtualRedTagVerifyTrailer',
    components: {
        FbxButton,
        FbxInput,
        VirtualRedTagHeader,
    },
    mixins: [trailerByNumber],
    data(): {
        isLoading: boolean;
        vin: string;
        licensePlate: string;
        vinAlert: string | null;
        plateAlert: string | null;
    } {
        return {
            isLoading: false,
            vin: '',
            licensePlate: '',
            vinAlert: null,
            plateAlert: null,
        };
    },
    computed: {
        ...mapGetters({
            selectedTrailerSelectionMethod: 'getTrailerSelectionMethod',
        }),
        hasLicensePlateNumber(): boolean {
            return trailerHasLicensePlateNumber(this.trailer ?? undefined);
        },
        hasVin(): boolean {
            return trailerHasVin(this.trailer ?? undefined);
        },
        cannotVerify(): boolean {
            return !this.hasLicensePlateNumber && !this.hasVin;
        },
        vinOrPlateText(): string {
            return vinOrPlateVerificationText(
                this.hasVin,
                this.hasLicensePlateNumber
            );
        },
        isInvalidInput(): boolean {
            return (
                (this.licensePlate.length < 5 && this.vin.length < 5) ||
                this.vinAlert != null ||
                this.plateAlert != null ||
                this.isLoading
            );
        },
    },
    async created(): Promise<void> {
        this.isLoading = true;
        await this.fetchTrailerByNumber();
        this.isLoading = false;
    },
    methods: {
        navigateBack(): void {
            this.$router.back();
        },
        onVinChanged(value: string): void {
            this.vin = value ?? '';
            this.vinAlert = null;
        },
        onPlateChanged(value: string): void {
            this.licensePlate = value ?? '';
            this.plateAlert = null;
        },
        validateVin(): boolean {
            if (isVinMatch(this.trailer ?? undefined, this.vin)) {
                return true;
            }

            if (this.vin !== '') {
                this.vinAlert = 'Incorrect VIN!';
            }

            return false;
        },
        validatePlate(): boolean {
            if (isPlateMatch(this.trailer ?? undefined, this.licensePlate)) {
                return true;
            }

            if (this.licensePlate !== '') {
                this.plateAlert = 'Incorrect Plate!';
            }

            return false;
        },
        async onContinue(): Promise<void> {
            if (this.isInvalidInput || !this.trailer) {
                return;
            }

            if (!this.validateVin() && !this.validatePlate()) {
                return;
            }

            this.isLoading = true;

            // Verifying by VIN or plate does not change how the driver found the
            // trailer, so a QR scan that lands here stays a QR scan in analytics.
            await this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                trailer: this.trailer,
                trailerSelectionMethod:
                    this.selectedTrailerSelectionMethod ??
                    TrailerSelectionMethod.manual,
            });

            this.$router.push({
                name: 'VirtualRedTagConfirmation',
                params: { trailerNumber: this.trailer.trailerNumber },
                query: {
                    verified: 'true',
                    trailerCompany: this.trailer.company,
                },
            });
        },
    },
});
